import { useEffect, useState, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import api from '../utils/api';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Printer, CalendarDays, Globe, Loader2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { getMonthGrid, getWeekBars, buildContinuousWeeks } from '../utils/calendarGrid';
import { getContrastColor } from '../utils/color';
import GoogleCalendarView from '../components/GoogleCalendarView';

const COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6','#a78bfa','#34d399','#60a5fa','#f9a8d4'];
const COMMON_COLOR = '#ffffff';

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function getMemberColor(id) { return COLORS[(id || 0) % COLORS.length]; }
function getItemColor(item) { return item.isCommon ? COMMON_COLOR : getMemberColor(item.adminId); }
function getItemTextColor(item) { return getContrastColor(getItemColor(item)); }

function koreaToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const EMPTY_FORM = { title: '', description: '', startDate: '', endDate: '', allDay: true, startTime: '', endTime: '', adminId: '', isCommon: false };

// ── 프린트 뷰 (연속 캘린더) ────────────────────────────────────
const BAR_H = 18;
const DAY_H = 24;

function PrintView({ config, schedules, members }) {
  const todayStr = koreaToday();
  const weeks = buildContinuousWeeks(config.fromYear, config.fromMonth, config.toYear, config.toMonth);

  const title = config.fromYear === config.toYear && config.fromMonth === config.toMonth
    ? `${config.fromYear}년 ${config.fromMonth + 1}월`
    : `${config.fromYear}년 ${config.fromMonth + 1}월 ~ ${config.toYear}년 ${config.toMonth + 1}월`;

  return (
    <div style={{ fontFamily: 'sans-serif', background: '#fff', padding: 16 }}>
      {/* 제목 + 범례 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: '#111', margin: 0 }}>{title}</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {members.map(mb => (
            <span key={mb.id} style={{ fontSize: 9, display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: getMemberColor(mb.id), display: 'inline-block', flexShrink: 0 }} />
              {mb.name || mb.username}
            </span>
          ))}
        </div>
      </div>

      {/* 요일 헤더 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '2px solid #333', borderTop: '1px solid #ddd' }}>
        {WEEKDAYS.map((d, i) => (
          <div key={d} style={{ textAlign: 'center', padding: '3px 0', fontSize: 9, fontWeight: 700,
            color: i === 0 ? '#c53030' : i === 6 ? '#2b6cb0' : '#444' }}>{d}</div>
        ))}
      </div>

      {/* 연속 주 */}
      {weeks.map((weekCells, wi) => {
        const barCells = weekCells.map(c => c.inRange ? c : null);
        const bars = getWeekBars(barCells, schedules, 'item');
        const maxRow = bars.reduce((m, b) => Math.max(m, b.row), -1) + 1;
        const rowH = Math.max(48, DAY_H + maxRow * (BAR_H + 2) + 4);

        return (
          <div key={wi} style={{ position: 'relative', minHeight: rowH, borderBottom: '1px solid #ddd' }}>
            {/* 배경 */}
            <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {weekCells.map((cell, di) => (
                <div key={di} style={{
                  borderRight: di < 6 ? '1px solid #eee' : 'none',
                  borderLeft: cell.isNewMonth && cell.inRange && di > 0 ? '2px solid #818cf8' : 'none',
                  backgroundColor: !cell.inRange ? '#f3f3f3' : '#fff',
                }} />
              ))}
            </div>

            {/* 날짜 숫자 */}
            <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {weekCells.map((cell, di) => {
                const isToday = cell.dateStr === todayStr;
                const isMonthStart = cell.isNewMonth && cell.inRange;
                return (
                  <div key={di} style={{ padding: '2px 3px', borderLeft: isMonthStart && di > 0 ? '2px solid #818cf8' : 'none' }}>
                    {isMonthStart && (
                      <div style={{ fontSize: 7, fontWeight: 700, color: '#4338ca', lineHeight: 1, marginBottom: 1 }}>
                        {cell.month + 1}월
                      </div>
                    )}
                    <span style={{
                      fontSize: 10, fontWeight: cell.inRange ? 600 : 400,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: '50%',
                      color: !cell.inRange ? '#bbb'
                        : isToday ? '#fff'
                        : di === 0 ? '#c53030'
                        : di === 6 ? '#2b6cb0'
                        : '#222',
                      backgroundColor: isToday ? '#667eea' : 'transparent',
                    }}>
                      {cell.day}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* 일정 바 */}
            <div style={{ position: 'relative', zIndex: 2, marginTop: 1 }}>
              {Array.from({ length: maxRow }, (_, ri) => (
                <div key={ri} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', height: BAR_H, marginBottom: 2 }}>
                  {bars.filter(b => b.row === ri).map(b => {
                    const color = getMemberColor(b.item.adminId);
                    const ownerName = b.item.owner?.name || b.item.owner?.username || '';
                    return (
                      <div key={b.item.id} style={{
                        gridColumn: `${b.startCol + 1} / span ${b.span}`,
                        backgroundColor: color, color: '#fff', fontSize: 8,
                        padding: '1px 3px', display: 'flex', alignItems: 'center', gap: 2,
                        overflow: 'hidden', whiteSpace: 'nowrap', margin: '0 1px',
                        borderRadius: b.isStart && b.isEnd ? 3 : b.isStart ? '3px 0 0 3px' : b.isEnd ? '0 3px 3px 0' : 0,
                      }}>
                        {ownerName && (
                          <span style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 2, padding: '0 2px', flexShrink: 0, fontSize: 7 }}>
                            {ownerName}
                          </span>
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.item.title}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── 메인 컴포넌트 ──────────────────────────────────────────────
export default function SchedulePage() {
  const todayStr = koreaToday();
  const me = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const isSuperadmin = me.role === 'superadmin';
  const [activeTab, setActiveTab] = useState('personal'); // 'personal' | 'google'

  const [year, setYear] = useState(() => parseInt(todayStr.split('-')[0]));
  const [month, setMonth] = useState(() => parseInt(todayStr.split('-')[1]) - 1);
  const [schedules, setSchedules] = useState([]);
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // 프린트 상태
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printConfig, setPrintConfig] = useState(null);
  const [printSchedules, setPrintSchedules] = useState([]);
  const printPending = useRef(false);
  const calWrapRef = useRef(null);
  const [calH, setCalH] = useState(0);

  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);
  const weeks = useMemo(() => { const r = []; for (let i = 0; i < grid.length; i += 7) r.push(grid.slice(i, i + 7)); return r; }, [grid]);

  const loadSchedules = useCallback(() => {
    const startDate = grid[0].dateStr;
    const endDate = grid[grid.length - 1].dateStr;
    api.get('/schedule', { params: { startDate, endDate } }).then(r => setSchedules(r.data));
  }, [grid]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);
  useEffect(() => { api.get('/schedule/members').then(r => setMembers(r.data)); }, []);

  useLayoutEffect(() => {
    const el = calWrapRef.current;
    if (!el) return;
    setCalH(el.clientHeight);
    const ro = new ResizeObserver(() => setCalH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const goThisMonth = () => { setYear(parseInt(todayStr.split('-')[0])); setMonth(parseInt(todayStr.split('-')[1]) - 1); };

  const weekBarsMap = useMemo(() => weeks.map(w => getWeekBars(w, schedules, 'item')), [weeks, schedules]);

  // 드래그앤 드롭
  const dragRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const handleBarDragStart = (e, item) => {
    dragRef.current = item;
    setDraggingId(item.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleBarDragEnd = () => {
    setDraggingId(null);
    setDragOverDate(null);
    dragRef.current = null;
  };
  const handleCellDrop = async (e, cell) => {
    e.preventDefault();
    setDragOverDate(null);
    const item = dragRef.current;
    if (!item) return;
    dragRef.current = null;
    setDraggingId(null);
    const days = Math.round((new Date(cell.dateStr + 'T00:00:00') - new Date(item.startDate + 'T00:00:00')) / 86400000);
    if (days === 0) return;
    const newStart = addDays(item.startDate, days);
    const newEnd = addDays(item.endDate, days);
    setSchedules(prev => prev.map(s => s.id === item.id ? { ...s, startDate: newStart, endDate: newEnd } : s));
    try {
      await api.put(`/schedule/${item.id}`, { title: item.title, description: item.description, startDate: newStart, endDate: newEnd, allDay: item.allDay, startTime: item.startTime, endTime: item.endTime, adminId: item.adminId, isCommon: item.isCommon });
    } catch {
      loadSchedules();
    }
  };

  const openAdd = (cell) => {
    if (!cell.isCurrentMonth) return;
    setEditingItem(null);
    setForm({ ...EMPTY_FORM, startDate: cell.dateStr, endDate: cell.dateStr, adminId: String(me.id) });
    setShowForm(true);
  };

  const openEdit = (item) => {
    // 공통 일정은 슈퍼관리자만 수정
    if (item.isCommon && !isSuperadmin) return;
    if (!item.isCommon && item.adminId !== me.id && !isSuperadmin) return;
    setEditingItem(item);
    setForm({ title: item.title, description: item.description || '', startDate: item.startDate, endDate: item.endDate,
      allDay: item.allDay !== false, startTime: item.startTime || '', endTime: item.endTime || '',
      adminId: String(item.adminId), isCommon: item.isCommon || false });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim()) return;
    // form 스냅샷 — async 실행 중 상태 변경에 대비
    const snapshot = { ...form };
    try {
      if (editingItem) {
        await api.put(`/schedule/${editingItem.id}`, snapshot);
      } else {
        await api.post('/schedule', {
          ...snapshot,
          adminId: snapshot.isCommon ? String(me.id) : (snapshot.adminId || me.id),
        });
      }
      setShowForm(false);
      // 저장 후 전체 데이터 강제 재로딩
      const startDate = grid[0].dateStr;
      const endDate = grid[grid.length - 1].dateStr;
      const r = await api.get('/schedule', { params: { startDate, endDate } });
      setSchedules(r.data);
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.message || e.message));
    }
  };

  const del = async () => {
    if (!editingItem || !confirm('삭제하시겠습니까?')) return;
    await api.delete(`/schedule/${editingItem.id}`);
    setShowForm(false);
    loadSchedules();
  };

  // 프린트 실행
  const initiatePrint = async (cfg) => {
    const startDate = `${cfg.fromYear}-${String(cfg.fromMonth + 1).padStart(2, '0')}-01`;
    const toLastDay = new Date(cfg.toYear, cfg.toMonth + 1, 0).getDate();
    const endDate = `${cfg.toYear}-${String(cfg.toMonth + 1).padStart(2, '0')}-${String(toLastDay).padStart(2, '0')}`;
    const r = await api.get('/schedule', { params: { startDate, endDate } });
    setPrintSchedules(r.data);
    setPrintConfig(cfg);
    printPending.current = true;
    setShowPrintModal(false);
  };

  useEffect(() => {
    if (!printConfig || !printPending.current) return;
    printPending.current = false;
    const t = setTimeout(() => {
      window.print();
      const reset = () => setPrintConfig(null);
      window.addEventListener('afterprint', reset, { once: true });
    }, 150);
    return () => clearTimeout(t);
  }, [printConfig, printSchedules]);

  const isThisMonth = year === parseInt(todayStr.split('-')[0]) && month === parseInt(todayStr.split('-')[1]) - 1;

  return (
    <>
      {/* ── 프린트 뷰 (화면엔 숨김, 인쇄시만 표시) ── */}
      <div className="cal-print-only">
        {printConfig && <PrintView config={printConfig} schedules={printSchedules} members={members} />}
      </div>

      {/* ── 일반 뷰 ── */}
      <div className="cal-no-print flex flex-col flex-1 min-h-0">
        {/* 탭 */}
        <div className="flex gap-1 mb-4 flex-shrink-0">
          <button
            onClick={() => setActiveTab('personal')}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'personal' ? 'bg-brand/20 text-brand' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <CalendarDays size={14} /> 팀 일정
          </button>
          <button
            onClick={() => setActiveTab('google')}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'google' ? 'bg-brand/20 text-brand' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >
            <Globe size={14} /> Google 캘린더
          </button>
        </div>

        {activeTab === 'google' && <GoogleCalendarView />}
        {activeTab === 'personal' && <>

        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-1">
            <CalendarDays size={20} className="text-brand flex-shrink-0 mr-1" />
            <button onClick={prevMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"><ChevronLeft size={20} /></button>
            <h1 className="text-xl font-bold text-white min-w-[140px] text-center">{year}년 {month + 1}월</h1>
            <button onClick={nextMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"><ChevronRight size={20} /></button>
            {isThisMonth ? (
              <span className="px-2 py-0.5 text-[10px] text-brand bg-brand/10 rounded font-medium ml-1">이번 달</span>
            ) : (
              <button onClick={goThisMonth} className="px-2 py-1 text-xs text-brand border border-brand/30 rounded hover:bg-brand/10 ml-1">이번 달로</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm border border-gray-500" style={{ backgroundColor: COMMON_COLOR }} />
                <span className="text-gray-400 text-xs">공통</span>
              </div>
              {members.map(m => (
                <div key={m.id} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: getMemberColor(m.id) }} />
                  <span className="text-gray-400 text-xs">{m.name || m.username}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowPrintModal(true)}
              className="flex items-center gap-1 p-1.5 text-gray-400 hover:text-white text-xs rounded-lg hover:bg-gray-800 border border-gray-800">
              <Printer size={13} /><span className="hidden sm:inline">프린트</span>
            </button>
          </div>
        </div>

        {/* 요일 헤더 */}
        <div className="grid grid-cols-7 border-b border-gray-800 flex-shrink-0">
          {WEEKDAYS.map((d, i) => (
            <div key={d} className={`text-center py-2.5 text-sm font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-500'}`}>{d}</div>
          ))}
        </div>

        {/* 캘린더 */}
        {(() => {
          const frVals = weeks.map((_, wi) => {
            const maxRow = weekBarsMap[wi].reduce((m, b) => Math.max(m, b.row), -1) + 1;
            return Math.max(80, 44 + maxRow * 26);
          });
          const totalFr = frVals.reduce((s, v) => s + v, 0);
          return (
        <div ref={calWrapRef} className="flex-1 min-h-0">
          {weeks.map((weekCells, wi) => {
            const bars = weekBarsMap[wi];
            const maxRow = bars.reduce((m, b) => Math.max(m, b.row), -1) + 1;
            const h = calH > 0 ? Math.floor(frVals[wi] / totalFr * calH) : 0;
            return (
              <div key={wi} style={{ height: h, overflow: 'hidden' }} className="border-b border-gray-800/50 relative">
                {/* 배경 셀 */}
                <div className="absolute inset-0 grid grid-cols-7">
                  {weekCells.map((cell, di) => (
                    <div key={di}
                      className={`${di < 6 ? 'border-r border-gray-800/30' : ''} ${cell.isCurrentMonth ? 'hover:bg-gray-800/10 cursor-pointer group' : 'bg-gray-950/30'} ${dragOverDate === cell.dateStr ? 'bg-brand/20' : ''}`}
                      onDoubleClick={() => openAdd(cell)}
                      onDragOver={e => { e.preventDefault(); setDragOverDate(cell.dateStr); }}
                      onDrop={e => handleCellDrop(e, cell)}
                    >
                      {cell.isCurrentMonth && (
                        <div className="flex justify-end p-0.5">
                          <button onClick={e => { e.stopPropagation(); openAdd(cell); }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-600 hover:text-brand rounded transition-opacity">
                            <Plus size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* 날짜 숫자 */}
                <div className="relative z-10 grid grid-cols-7 pointer-events-none">
                  {weekCells.map((cell, di) => {
                    const isToday = cell.dateStr === todayStr;
                    return (
                      <div key={di} className="px-1.5 pt-1 pb-0.5">
                        <span className={`text-sm font-semibold inline-flex items-center justify-center w-7 h-7 rounded-full ${
                          isToday ? 'bg-brand text-white'
                            : !cell.isCurrentMonth ? 'text-gray-700'
                            : di === 0 ? 'text-red-400'
                            : di === 6 ? 'text-blue-400'
                            : 'text-gray-300'
                        }`}>{cell.day}</span>
                      </div>
                    );
                  })}
                </div>

                {/* 일정 바 */}
                <div className="relative z-10 mt-0.5">
                  {Array.from({ length: maxRow }, (_, ri) => (
                    <div key={ri} className="grid grid-cols-7" style={{ height: '26px' }}>
                      {bars.filter(b => b.row === ri).map(b => {
                        const color = getItemColor(b.item);
                        const textColor = getItemTextColor(b.item);
                        const isCommon = b.item.isCommon;
                        const ownerName = isCommon ? '공통' : (b.item.owner?.name || b.item.owner?.username || '');
                        const isEditable = isCommon ? isSuperadmin : (b.item.adminId === me.id || isSuperadmin);
                        const timeLabel = !b.item.allDay && b.item.startTime ? b.item.startTime : '';
                        return (
                          <div key={b.item.id}
                            draggable={isEditable}
                            onDragStart={isEditable ? e => handleBarDragStart(e, b.item) : undefined}
                            onDragEnd={isEditable ? handleBarDragEnd : undefined}
                            className={`h-[23px] mx-[1px] flex items-center text-[11px] font-medium truncate transition-all select-none ${isEditable ? 'cursor-grab active:cursor-grabbing hover:brightness-95' : 'cursor-default'} ${b.isStart && b.isEnd ? 'rounded-md' : b.isStart ? 'rounded-l-md' : b.isEnd ? 'rounded-r-md' : ''} ${draggingId === b.item.id ? 'opacity-40' : ''}`}
                            style={{ gridColumn: `${b.startCol + 1} / span ${b.span}`, backgroundColor: color, color: textColor, border: isCommon ? '1px solid #d1d5db' : 'none' }}
                            onClick={() => openEdit(b.item)}
                          >
                            {(isCommon || (isSuperadmin && ownerName)) && (
                              <span className="bg-black/25 text-[10px] leading-none px-1 py-[2px] rounded flex-shrink-0 ml-[3px] font-semibold">{ownerName}</span>
                            )}
                            {timeLabel && (
                              <span className="bg-black/20 text-[10px] leading-none px-1 py-[2px] rounded flex-shrink-0 ml-[3px]">{timeLabel}</span>
                            )}
                            <span className="ml-1.5 truncate">{b.item.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
          );
        })()}

        {/* 일정 추가/수정 모달 */}
        {showForm && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-bold text-sm">{editingItem ? '일정 수정' : '일정 추가'}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white"><X size={16} /></button>
              </div>
              <div className="space-y-3 w-full overflow-x-hidden">
                {/* 슈퍼관리자: 공통 일정 토글 */}
                {isSuperadmin && (
                  <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                    style={{ backgroundColor: form.isCommon ? 'rgba(100,116,139,0.15)' : undefined }}>
                    <input type="checkbox" checked={form.isCommon}
                      onChange={e => setForm(f => ({ ...f, isCommon: e.target.checked, adminId: f.adminId || String(me.id) }))}
                      className="accent-brand" />
                    <span className="text-sm font-medium" style={{ color: form.isCommon ? COMMON_COLOR : '#9ca3af' }}>공통 일정 (전 직원 공유)</span>
                  </label>
                )}
                {/* 슈퍼관리자: 작성자 선택 (공통 일정 아닐 때만) */}
                {isSuperadmin && !form.isCommon && (
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">작성자</label>
                    <select value={form.adminId} onChange={e => setForm(f => ({ ...f, adminId: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand">
                      {members.map(m => <option key={m.id} value={m.id}>{m.name || m.username}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">제목</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="일정 제목"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
                  <input type="checkbox" checked={form.allDay} onChange={e => setForm(f => ({ ...f, allDay: e.target.checked, startTime: '', endTime: '' }))} className="accent-brand" />
                  종일
                </label>
                <div className="flex flex-col gap-2">
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">시작일</label>
                    <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value, endDate: e.target.value > f.endDate ? e.target.value : f.endDate }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">종료일</label>
                    <input type="date" value={form.endDate} min={form.startDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                  </div>
                </div>
                {!form.allDay && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-gray-400 text-xs mb-1 block">시작 시간</label>
                      <input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs mb-1 block">종료 시간</label>
                      <input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">메모 (선택)</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand resize-none" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-5">
                {editingItem ? (
                  <button onClick={del} className="text-red-400 text-xs hover:text-red-300 flex items-center gap-1"><Trash2 size={12} /> 삭제</button>
                ) : <div />}
                <div className="flex gap-2">
                  <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
                  <button onClick={save} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">저장</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 프린트 설정 모달 */}
        {showPrintModal && (
          <PrintConfigModal
            defaultYear={year} defaultMonth={month}
            onConfirm={initiatePrint}
            onClose={() => setShowPrintModal(false)}
          />
        )}
      </>}
    </div>
  </>
  );
}

function PrintConfigModal({ defaultYear, defaultMonth, onConfirm, onClose }) {
  const [fromYear, setFromYear] = useState(defaultYear);
  const [fromMonth, setFromMonth] = useState(defaultMonth);
  const [toYear, setToYear] = useState(defaultYear);
  const [toMonth, setToMonth] = useState(defaultMonth);
  const months = Array.from({ length: 12 }, (_, i) => i);
  const years = Array.from({ length: 5 }, (_, i) => defaultYear - 1 + i);

  const isValid = toYear > fromYear || (toYear === fromYear && toMonth >= fromMonth);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-xs overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm flex items-center gap-2"><Printer size={14} /> 캘린더 프린트</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-xs mb-1 block">시작 월</label>
            <div className="flex gap-2">
              <select value={fromYear} onChange={e => setFromYear(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {years.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
              <select value={fromMonth} onChange={e => setFromMonth(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {months.map(m => <option key={m} value={m}>{m + 1}월</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">종료 월</label>
            <div className="flex gap-2">
              <select value={toYear} onChange={e => setToYear(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {years.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
              <select value={toMonth} onChange={e => setToMonth(parseInt(e.target.value))}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm focus:outline-none">
                {months.map(m => <option key={m} value={m}>{m + 1}월</option>)}
              </select>
            </div>
          </div>
          {!isValid && <p className="text-red-400 text-xs">종료 월이 시작 월보다 앞입니다.</p>}
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
          <button onClick={() => isValid && onConfirm({ fromYear, fromMonth, toYear, toMonth })} disabled={!isValid}
            className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80 disabled:opacity-40 flex items-center gap-1.5">
            <Printer size={13} /> 프린트
          </button>
        </div>
      </div>
    </div>
  );
}
